import React, { PureComponent, createRef } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';

import { closest } from 'utils';

import { ParagraphSmall } from 'components/Typography';
import ContentPlaceholder from 'components/ContentPlaceholder';

import { Label, Input, Wrapper } from './styles';

class ConverterInput extends PureComponent {
  constructor(props) {
    super(props);

    this.state = { isFocused: false };

    this.labelRef = createRef();
    this.inputRef = createRef();

    this.noDataLabel = props.intl.formatMessage({
      id: 'currencyConverter.noData.input.placeholder',
    });
  }

  componentDidMount() {
    this.setListeners();
  }

  componentWillUnmount() {
    this.removeListeners();
  }

  setListeners = () => {
    document.addEventListener('mousedown', this.handleClickOutside);
    document.addEventListener('touchend', this.handleClickOutside);
  };

  removeListeners = () => {
    document.removeEventListener('mousedown', this.handleClickOutside);
    document.removeEventListener('touchend', this.handleClickOutside);
  };

  onFocus = () => {
    this.setState(
      {
        isFocused: true,
      },
      () => {
        this.inputRef.current.focus();
      }
    );
  };

  onBlur = () => {
    this.setState({
      isFocused: false,
    });
  };

  onChange = event => {
    const { onChange } = this.props;
    const { value } = event.target;

    const unformattedValue = this.unformatValue(value);

    onChange(unformattedValue);
  };

  round = value => Math.round(value * 10000) / 10000;

  getFormattedValue = value => {
    const { isFocused } = this.state;
    const { intl, currencyType } = this.props;

    if (value === '') return '';

    if (isFocused) {
      return value;
    }

    return intl
      .formatNumber(value, {
        maximumFractionDigits: currencyType === 'crypto' ? 8 : 2,
      })
      .replace(/,/g, '.');
  };

  unformatValue = value => {
    const result = value.match(/(\.|,)/g);

    const changedValue = value
      .replace(/\s/g, '')
      .replace(/,/g, '.')
      .replace(/[^0-9.]/g, '');

    return result && result.length > 1
      ? changedValue.replace(/\.$/, '')
      : changedValue;
  };

  handleClickOutside = event => {
    if (
      this.labelRef.current &&
      !closest(event.target, this.labelRef.current)
    ) {
      this.onBlur();
    }
  };

  onCurrencySelectorFocus = e => {
    e.stopPropagation();
    this.onBlur();
  };

  render() {
    const { isFocused } = this.state;
    const {
      value,
      label,
      sourceCurrency,
      targetCurrency,
      rate,
      type,
      noData,
      loading,
    } = this.props;

    const disabled =
      (type === 'target' && noData) ||
      (type === 'target' && sourceCurrency === targetCurrency);

    const isTipHidden = sourceCurrency === targetCurrency;

    const resultValue = disabled
      ? this.noDataLabel
      : this.getFormattedValue(value);

    return (
      <Wrapper>
        <Label
          ref={this.labelRef}
          isFocused={isFocused}
          onClick={this.onFocus}
          disabled={disabled}
          noData={noData}
          isTipHidden={isTipHidden}
        >
          <ParagraphSmall as="span">{label}</ParagraphSmall>

          {type === 'target' && loading ? (
            <ContentPlaceholder style={{ width: '121px', height: '23px' }} />
          ) : (
            <Input
              ref={this.inputRef}
              value={resultValue}
              type="tel"
              onChange={this.onChange}
              onBlur={this.onBlur}
              onFocus={this.onFocus}
              disabled={disabled}
            />
          )}

          {!noData && !loading && (
            <ParagraphSmall as="span">
              {`1 ${sourceCurrency} = ${this.round(rate)} ${targetCurrency}`}
            </ParagraphSmall>
          )}

          {loading && (
            <ContentPlaceholder style={{ width: '110px', height: '12px' }} />
          )}
        </Label>
      </Wrapper>
    );
  }
}

export default injectIntl(ConverterInput);

ConverterInput.propTypes = {
  type: PropTypes.oneOf(['source', 'target']).isRequired,
  currencyType: PropTypes.oneOf(['money', 'crypto']).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  sourceCurrency: PropTypes.string,
  targetCurrency: PropTypes.string,
  rate: PropTypes.number,
  onChange: PropTypes.func,
  noData: PropTypes.bool,
  loading: PropTypes.bool,

  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
    formatNumber: PropTypes.func,
  }).isRequired,
};

ConverterInput.defaultProps = {
  value: 0,
  label: '',
  sourceCurrency: '',
  targetCurrency: '',
  rate: 0,
  onChange: () => {},
  noData: false,
  loading: false,
};
